/**
 * useFocusTrap — keyboard focus management for modal dialogs.
 *
 * Promoted from the inline pattern in
 * `modules/platform/_components/NewImportSheet.tsx` after the
 * SCRUM-167 property slice grew a third dialog site
 * (`UnitStatusModal`, `DiscardConfirm` in the create wizard,
 * `ArchiveConfirmDialog` in the property detail Lifecycle tab) —
 * Codex called out the dialog focus-trap drift on the SCRUM-167 PR
 * pre-review.
 *
 * Behaviour, per WAI-ARIA Authoring Practices §3.9 (Dialog):
 *
 *   1. **Initial focus** lands on a caller-provided element (e.g.
 *      Cancel button), falling back to the first tabbable child of
 *      the container. Lets the dialog choose a safe landing target
 *      that does NOT commit an action on accidental Enter.
 *
 *   2. **Tab containment.** Tab from the last tabbable child cycles
 *      to the first; Shift+Tab from the first cycles to the last.
 *      Keyboard users can't tab behind the modal.
 *
 *   3. **Focus return.** On unmount, focus restores to whatever
 *      owned focus when the hook mounted (typically the dialog's
 *      opener button) so the user resumes from where they
 *      triggered the modal — a non-negotiable per AAA §3.9.
 *
 * Esc-to-close + backdrop-click-to-close stay caller-owned because
 * they need to interlock with the close handler (which is dialog-
 * specific). The hook only manages focus.
 *
 * Honest scope:
 *   - Skips visibility filtering (`offsetParent`, `getBoundingClientRect`)
 *     because jsdom doesn't compute layout in tests; the
 *     `disabled` + `tabindex="-1"` exclusions are the cheap-but-
 *     correct filter for typical dialogs.
 *   - Does NOT install a global keydown listener for Escape — caller
 *     wires that to its own `onClose` so the hook stays
 *     single-responsibility.
 */

import * as React from 'react';

/**
 * Standard tab-stop selector. Disabled controls + explicit
 * `tabindex="-1"` are excluded.
 */
function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (container === null) return [];
  const selector = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  return Array.from(container.querySelectorAll<HTMLElement>(selector));
}

export interface UseFocusTrapOptions {
  /** When false, the hook does nothing — useful for late-mount dialogs. */
  readonly enabled?: boolean;
  /**
   * Optional explicit initial-focus target. When omitted, focus
   * lands on the first tabbable descendant of `containerRef`. Per
   * AAA §3.9, dialogs typically prefer Cancel as the initial-focus
   * landing pad so accidental Enter doesn't commit a destructive
   * action.
   */
  readonly initialFocusRef?: React.RefObject<HTMLElement | null>;
}

export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  options: UseFocusTrapOptions = {},
): void {
  const { enabled = true, initialFocusRef } = options;

  React.useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (container === null) return;

    // Capture the element that owned focus when the dialog opened so
    // we can return focus there on unmount. document.activeElement
    // narrows to `Element | null`; cast to HTMLElement because focus()
    // only exists on HTMLElement/SVGElement (sufficient for our DOM).
    const opener = document.activeElement as HTMLElement | null;

    // Initial focus — explicit target wins; otherwise first focusable.
    const explicit = initialFocusRef?.current ?? null;
    if (explicit !== null) {
      explicit.focus();
    } else {
      const focusables = getFocusableElements(container);
      const first = focusables[0];
      if (first !== undefined) first.focus();
    }

    // Tab containment. We attach to the container (not document) so
    // outside-modal Tab events don't fire the trap; only Tabs that
    // bubble from inside the dialog count.
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const focusables = getFocusableElements(container);
      if (focusables.length === 0) {
        // Nothing tabbable — swallow Tab so focus doesn't escape.
        event.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement as HTMLElement | null;
      // The container itself may have programmatic focus (e.g. when
      // the user just opened the dialog and we focused a non-focusable
      // h2 by accident). Coerce to the appropriate edge.
      if (event.shiftKey) {
        if (active === first || !container.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !container.contains(active)) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      // Focus return — restore the opener so the user doesn't lose
      // their place in the page after closing. Guard against the
      // opener being unmounted (focus() throws on detached nodes in
      // some environments) by checking it's still in the document.
      if (opener !== null && document.body.contains(opener)) {
        opener.focus();
      }
    };
  }, [containerRef, enabled, initialFocusRef]);
}
